import type { ChatType } from '@form-factory/schema';
import {
  createChatSchema,
  updateChatSchema,
  type CreateChatDTO,
  type UpdateChatDTO,
} from '@form-factory/schema/contracts';
import { Role } from '@form-factory/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { File, FileScan } from 'lucide-react';
import { memo, useCallback, useMemo, useRef } from 'react';
import type { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { MetaFields } from '@/components/fields/MetaFields';
import { createLazyFileUploadManager } from '@/components/fileUpload/adapters/lazyFileUploadAdapter';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { Protected } from '@/components/protected/Protected';
import { useBoundStore } from '@/store/store';

import { BasicConfigFields } from './formFields/BasicConfigFields';
import { ChatbotScriptFields } from './formFields/ChatbotScriptFields';
import { ChatPlacementFields } from './formFields/ChatPlacementFields';
import { ChatThemeFields } from './formFields/ChatThemeFields';
import { EvaluationFields } from './formFields/EvaluationFields';
import { OmnichannelConfigFields } from './formFields/OmnichannelConfigFields';
import { OverridesFields } from './formFields/OverridesFields';
import { PersonalizationFields } from './formFields/PersonalizationFields';
import { SuperAdminFields } from './formFields/SuperAdminFields';
import { ToolConfigurationFields } from './formFields/ToolConfigurationFields';
import { useChatMutation } from '../useChatMutation';

export type ChatFormProps = {
  data?: ChatType;
};

export const ChatForm = memo(function ChatForm({ data }: ChatFormProps) {
  const { t } = useLingui();
  const { setChat } = useBoundStore(
    useShallow(state => ({
      setChat: state.setChat,
    })),
  );

  const fileUploadManager = useRef(createLazyFileUploadManager());
  const uploadAdapters = useMemo(
    () => ({
      avatar: fileUploadManager.current.getAdapter('avatar'),
      logo: fileUploadManager.current.getAdapter('logo'),
      bubble: fileUploadManager.current.getAdapter('bubble'),
    }),
    [],
  );

  const {
    create: { mutateAsync: create },
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
  } = useChatMutation();

  /**
   * Commit (upload/delete) images (avatar, bubbleLogo and logo).
   *
   * It also returns `values` objects containing PublicUrl of uploaded images
   * in it's respective place, so for example, for avatar it updates `values.chatbotConfig.systemAvatar`
   */
  const commitFileValues = useCallback(
    async <T extends CreateChatDTO | UpdateChatDTO>(values: T): Promise<T> => {
      const results = await fileUploadManager.current.commitAll();

      // Use the last result from each adapter's queue (handles delete -> upload scenarios)
      const avatar = results.avatar?.at(-1);
      const logo = results.logo?.at(-1);
      const bubble = results.bubble?.at(-1);

      if (avatar) {
        // @ts-expect-error This is OK, unfortunate type inference
        values.chatbotConfig = {
          ...values?.chatbotConfig,
          systemAvatar: avatar.publicUrl,
        };
      }

      if (logo) {
        values.themeConfig = {
          ...values?.themeConfig,
          logo: logo.publicUrl,
        };
      }

      if (bubble) {
        values.themeConfig = {
          ...values?.themeConfig,
          logoBubble: bubble.publicUrl,
        };
      }

      return values;
    },
    [],
  );

  return (
    <EntityCRUDForm
      /**
       * We need to pass key to ChatForm to force re-render when chatId changes,
       * this is to properly reset initial form data when chatId changes.
       */
      key={data?.id}
      initialData={data as any}
      createSchema={createChatSchema as unknown as z.ZodTypeAny}
      updateSchema={updateChatSchema as unknown as z.ZodTypeAny}
      transformValues={(values: any) => {
        // Type assertion is necessary due to complex schema transformation
        const transformedValues = { ...values };

        if (transformedValues.toolEnabled) {
          // Convert toolEnabled object to disabledTools array
          // Only process configurable tools to prevent invalid tool IDs
          const configurableTools = new Set([
            'membership',
            'groupClass',
            'membership_status',
            'user_profile',
            'membership_registration',
            'membership_pause',
          ]);

          transformedValues.disabledTools = Object.entries(
            transformedValues.toolEnabled,
          )
            .filter(
              ([toolId, enabled]) =>
                enabled === false && configurableTools.has(toolId),
            )
            .map(([toolId]) => toolId);

          delete transformedValues.toolEnabled;
        }

        return transformedValues;
      }}
      onCreate={async values => {
        const fileValues = await commitFileValues(values);
        const newChat = await create(fileValues);

        // Set new chat as active
        setChat(newChat as any);

        // Return new chat
        return newChat as any;
      }}
      onUpdate={async values => {
        const fileValues = await commitFileValues(values);

        return update(fileValues as UpdateChatDTO);
      }}
      onRemove={remove}
      basePath='/chats'
      idParamName='chatId'
      allowEmptyStrings
    >
      <EntityCRUDForm.Col>
        <BasicConfigFields />
        <OmnichannelConfigFields />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <PersonalizationFields />
        <ToolConfigurationFields />
        <ChatThemeFields
          logoUploadAdapter={uploadAdapters.logo}
          avatarUploadAdapter={uploadAdapters.avatar}
          bubbleUploadAdapter={uploadAdapters.bubble}
          data={data}
        />
        <ChatPlacementFields />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <MetaFields data={data}>
          <Protected roles={[Role.SuperAdmin]}>
            <DisplayValue label={t`Documents count`} Icon={File}>
              {data?.documentsCount ?? '0'}
            </DisplayValue>
            <DisplayValue label={t`Embeddings count`} Icon={FileScan}>
              {data?.embeddingsCount ?? '0'}
            </DisplayValue>
          </Protected>
        </MetaFields>
        <ChatbotScriptFields data={data} />
        <Protected roles={[Role.SuperAdmin]}>
          <EvaluationFields data={data} />
          <SuperAdminFields />
          <OverridesFields />
        </Protected>
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
